import { stripeAPI } from './api_service';
import { APP_URLS, COMPANY_BRANDING } from './url_helper';
import { formatDate } from './UtilsFunctions';

export const normalizePaymentStatus = (status) => String(status || '').trim().toLowerCase();

export const isPendingPayment = (status) => normalizePaymentStatus(status) === 'pending';

export const isCompletedPayment = (status) => normalizePaymentStatus(status) === 'completed';

export const getStripeApiMessage = (payload, fallback = 'Something went wrong. Please try again.') => {
  if (!payload) {
    return fallback;
  }

  if (typeof payload === 'string') {
    return payload;
  }

  if (typeof payload.message === 'object' && payload.message?.message) {
    return payload.message.message;
  }

  if (typeof payload.message === 'string') {
    return payload.message;
  }

  if (payload instanceof Error && payload.message && payload.message !== '[object Object]') {
    return payload.message;
  }

  return fallback;
};

export const formatSubscriptionHistoryItem = (item, index = 0) => {
  const startDate = formatDate(item.subscription_start_date);
  const endDate = formatDate(item.subscription_end_date);
  const dateRange = `${startDate} - ${endDate}`;
  const planName = item.subscription_plan?.title || item.subscribed_plan_details?.plan_title || 'Unknown Plan';
  const formattedAmount = `${item.currency?.toUpperCase() || 'USD'} ${item.amount_paid}`;
  const rowId = item.id || index + 1;

  return {
    id: rowId,
    subscriberId: item.subscriber_id || item.id || null,
    invoiceNumber: `#${item.id || (1000 + index)}`,
    transactionId: item.payment_transaction_id || 'N/A',
    planName,
    planTitle: item.subscription_plan?.title || item.subscribed_plan_details?.plan_title || planName,
    billingCycle: item.billing_cycle || 'monthly',
    date: dateRange,
    paymentStatus: item.payment_status || 'Unknown',
    paymentMethod: item.payment_method || 'Unknown',
    amount: formattedAmount,
    invoiceId: item.invoice_id || null,
    autoRenewal: item.auto_renewal !== false,
  };
};

export const findFirstPendingSubscription = (subscriptionData) => {
  const history = subscriptionData?.subscription_history;
  if (!Array.isArray(history) || history.length === 0) {
    return null;
  }

  const firstItem = history[0];
  if (!isPendingPayment(firstItem?.payment_status)) {
    return null;
  }

  return formatSubscriptionHistoryItem(firstItem, 0);
};

export const getPendingPaymentDismissKey = (subscriberId) =>
  `dismissedPendingPayment_${subscriberId}`;

export const isPendingPaymentDismissed = (subscriberId) => {
  if (!subscriberId) {
    return false;
  }
  return sessionStorage.getItem(getPendingPaymentDismissKey(subscriberId)) === 'true';
};

export const dismissPendingPayment = (subscriberId) => {
  if (!subscriberId) {
    return;
  }
  sessionStorage.setItem(getPendingPaymentDismissKey(subscriberId), 'true');
};

export const startSubscriberCheckout = async (row, { mode, autoRenewal = true } = {}) => {
  if (!row?.subscriberId) {
    throw new Error('Subscriber details are not available for this payment.');
  }

  let customerEmail = '';
  try {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const user = JSON.parse(userData);
      customerEmail = user.email || user.user_email || '';
    }
  } catch (error) {
    console.error('Error parsing user data:', error);
  }

  const billingCycle = String(row.billingCycle || 'monthly').toLowerCase();
  const planTitle = row.planTitle || row.planName || 'Plan';
  const brandColor = mode === 'dark'
    ? COMPANY_BRANDING.BRAND_COLORS.DARK
    : COMPANY_BRANDING.BRAND_COLORS.LIGHT;

  const response = await stripeAPI.createCheckoutSessionFromSubscriber({
    subscriber_id: row.subscriberId,
    customer_email: customerEmail,
    auto_renewal: row.autoRenewal ?? autoRenewal,
    logo_url: COMPANY_BRANDING.LOGO_URL,
    brand_color: brandColor,
    success_url: `${window.location.origin}${APP_URLS.PAYMENT_SUCCESS}?plan=${encodeURIComponent(planTitle)}&billing=${billingCycle}&session_id={CHECKOUT_SESSION_ID}`,
    cancel_url: `${window.location.origin}${APP_URLS.PAYMENT_CANCEL}?plan=${encodeURIComponent(planTitle)}&billing=${billingCycle}`,
  }, {
    loadingMessage: 'Preparing payment...',
  });

  if (response.status === 'error') {
    throw new Error(getStripeApiMessage(response, 'Failed to start payment. Please try again.'));
  }

  const checkoutUrl = response.data?.checkout_url;
  const sessionId = response.data?.checkout_session_id || response.data?.session_id;

  if (response.status === 'success' && checkoutUrl) {
    if (sessionId) {
      localStorage.setItem('pendingStripeSessionId', sessionId);
    }
    window.location.href = checkoutUrl;
    return;
  }

  throw new Error(getStripeApiMessage(response, 'Failed to start payment. Please try again.'));
};
